import { useState } from "react";
import { useCromptonLens } from "../lib/realdata";
import type { LensBlock, LensRow } from "../lib/realdata";

type Cat = "MG" | "Fans";
type Layer = "brand" | "functional" | "emotional";

const QUAD_LABEL: Record<string, string> = {
  owned_strength:   "Owned strengths",
  opportunity:      "White-space opportunities",
  owned_low_value:  "Over-invested",
  low_priority:     "Low priority",
  missing_data:     "Not surveyed",
};

const QUAD_COLOR: Record<string, string> = {
  owned_strength:  "var(--teal)",
  opportunity:     "var(--coral)",
  owned_low_value: "var(--amber)",
  low_priority:    "var(--ink-40)",
  missing_data:    "var(--ink-20)",
};

const QUAD_SOFT: Record<string, string> = {
  owned_strength:  "var(--teal-tint)",
  opportunity:     "var(--coral-tint)",
  owned_low_value: "var(--amber-soft)",
  low_priority:    "var(--page-2)",
  missing_data:    "var(--page-2)",
};

export default function CromptonLens() {
  const lens = useCromptonLens();
  const [cat, setCat] = useState<Cat>("Fans");
  const [layer, setLayer] = useState<Layer>("brand");

  if (!lens) {
    return <div className="loading-card"><div className="lab muted">Loading Crompton lens…</div></div>;
  }

  const catData = lens.categories[cat];
  const block: LensBlock | null =
    layer === "brand" ? catData.brand :
    layer === "functional" ? catData.functional :
    catData.emotional;

  const availableLayers: Layer[] =
    cat === "MG" ? ["brand"] : ["brand", "functional", "emotional"];

  const opportunities = block?.rows.filter(r => r.quadrant === "opportunity") ?? [];
  const owned = block?.rows.filter(r => r.quadrant === "owned_strength") ?? [];
  const overInvested = block?.rows.filter(r => r.quadrant === "owned_low_value") ?? [];

  return (
    <div className="stagger">
      <section className="page-hero">
        <div className="kicker">v3 · Client lens</div>
        <h1>Where should <em className="warn">Crompton</em> spend its next marketing rupee?</h1>
        <p className="muted">
          Pulse joins importance ratings (A2c) with brand-imagery share (A3) to plot every attribute in
          a four-quadrant action map. <strong style={{ color: "var(--coral)" }}>Coral dots</strong> are white-space
          opportunities - attributes that matter to consumers but Crompton hasn't claimed yet.
          <strong style={{ color: "var(--teal)" }}> Teal dots</strong> are owned strengths to defend.
        </p>
      </section>

      <section className="lens-toggles">
        <div className="lens-toggle">
          <span className="muted">Category</span>
          <button className={"chip" + (cat === "MG" ? " active" : "")} onClick={() => setCat("MG")}>Mixer Grinder</button>
          <button className={"chip" + (cat === "Fans" ? " active" : "")} onClick={() => setCat("Fans")}>Fans</button>
        </div>
        <div className="lens-toggle">
          <span className="muted">Attribute layer</span>
          {availableLayers.map((l) => (
            <button key={l}
              className={"chip" + (layer === l ? " active" : "") + (availableLayers.includes(l) ? "" : " disabled")}
              onClick={() => setLayer(l)}
              disabled={!availableLayers.includes(l)}>
              {l === "brand" ? "Brand" : l === "functional" ? "Functional" : "Emotional"}
            </button>
          ))}
        </div>
      </section>

      {!block && (
        <section className="card-empty">
          <div>
            <h3>Not available for this layer</h3>
            <p className="muted">{("note" in catData && catData.note) || "The brand-imagery survey for this category did not cover this attribute layer."}</p>
          </div>
        </section>
      )}

      {block && <>
        <section className="lens-summary">
          <div className="ls-card teal">
            <div className="ls-num">{owned.length}</div>
            <div className="ls-lab">Owned strengths</div>
            <div className="ls-sub muted">High importance + Crompton owns</div>
          </div>
          <div className="ls-card coral">
            <div className="ls-num">{opportunities.length}</div>
            <div className="ls-lab">White-space opportunities</div>
            <div className="ls-sub muted">High importance + low Crompton ownership</div>
          </div>
          <div className="ls-card amber">
            <div className="ls-num">{overInvested.length}</div>
            <div className="ls-lab">Over-invested</div>
            <div className="ls-sub muted">Low importance + high Crompton ownership</div>
          </div>
          <div className="ls-card neutral">
            <div className="ls-num">{block.rows.length}</div>
            <div className="ls-lab">Attributes mapped</div>
            <div className="ls-sub muted">Median importance: {block.imp_median.toFixed(2)} · Median ownership: {block.own_median.toFixed(0)}%</div>
          </div>
        </section>

        <QuadrantChart block={block} />

        <CalloutLists block={block} />

        <section className="lens-table-card">
          <h3>All attributes - detailed scores</h3>
          <table className="cmp-table">
            <thead>
              <tr>
                <th style={{ textAlign: "left" }}>Attribute</th>
                <th>Importance</th>
                <th>Crompton</th>
                <th>Top competitor</th>
                <th>Gap</th>
                <th>Quadrant</th>
              </tr>
            </thead>
            <tbody>
              {block.rows.slice().sort((a, b) =>
                (b.importance ?? 0) - (a.importance ?? 0)
              ).map((r) => {
                const gap = (r.crompton_share ?? 0) - (r.top_competitor_share ?? 0);
                const sign = gap > 0 ? "+" : "";
                return (
                  <tr key={r.code}>
                    <td style={{ textAlign: "left" }}>{r.label}</td>
                    <td>{r.importance?.toFixed(2) ?? "-"}</td>
                    <td>{r.crompton_share != null ? `${r.crompton_share.toFixed(1)}%` : "-"}</td>
                    <td>
                      {r.top_competitor && r.top_competitor_share != null
                        ? `${r.top_competitor} (${r.top_competitor_share.toFixed(1)}%)`
                        : "-"}
                    </td>
                    <td className={gap >= 0 ? "pos" : "neg"}>{sign}{gap.toFixed(1)}pt</td>
                    <td>
                      <span className="pill" style={{
                        background: QUAD_SOFT[r.quadrant],
                        color: QUAD_COLOR[r.quadrant],
                      }}>{QUAD_LABEL[r.quadrant]}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>
      </>}
    </div>
  );
}

// Quadrant chart
function QuadrantChart({ block }: { block: LensBlock }) {
  const W = 760, H = 540, P = 60;

  // Domains - dynamically scaled to data + padding
  const imps = block.rows.map(r => r.importance ?? 0).filter(v => v > 0);
  const owns = block.rows.map(r => r.crompton_share ?? 0).filter(v => v > 0);
  const xLo = Math.min(...imps) - 0.15;
  const xHi = Math.max(...imps) + 0.15;
  const yLo = Math.max(0, Math.min(...owns) - 5);
  const yHi = Math.max(...owns) + 5;

  const x = (v: number) => P + ((v - xLo) / (xHi - xLo)) * (W - 2 * P);
  const y = (v: number) => H - P - ((v - yLo) / (yHi - yLo)) * (H - 2 * P);

  return (
    <section className="quad-card">
      <h3>Importance vs Crompton brand-imagery share</h3>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ maxHeight: 580 }}>
        {/* Quadrant background fills */}
        <rect x={x(block.imp_median)} y={y(yHi)} width={x(xHi) - x(block.imp_median)} height={y(block.own_median) - y(yHi)} fill="var(--teal-tint)" opacity="0.5" />
        <rect x={x(xLo)} y={y(yHi)} width={x(block.imp_median) - x(xLo)} height={y(block.own_median) - y(yHi)} fill="var(--amber-soft)" opacity="0.4" />
        <rect x={x(block.imp_median)} y={y(block.own_median)} width={x(xHi) - x(block.imp_median)} height={y(yLo) - y(block.own_median)} fill="var(--coral-tint)" opacity="0.5" />
        <rect x={x(xLo)} y={y(block.own_median)} width={x(block.imp_median) - x(xLo)} height={y(yLo) - y(block.own_median)} fill="var(--page-2)" opacity="0.7" />

        {/* Median lines */}
        <line x1={x(block.imp_median)} y1={P} x2={x(block.imp_median)} y2={H - P} stroke="var(--ink-20)" strokeDasharray="4 4" />
        <line x1={P} y1={y(block.own_median)} x2={W - P} y2={y(block.own_median)} stroke="var(--ink-20)" strokeDasharray="4 4" />

        {/* Quadrant titles */}
        <text x={W - P - 6} y={P + 18} textAnchor="end" fontSize="11" fontWeight={600} fill="var(--teal-ink)">OWNED STRENGTHS</text>
        <text x={W - P - 6} y={H - P - 6} textAnchor="end" fontSize="11" fontWeight={600} fill="var(--coral-ink)">WHITE-SPACE OPPORTUNITIES</text>
        <text x={P + 6} y={P + 18} fontSize="11" fontWeight={600} fill="var(--amber)">OVER-INVESTED</text>
        <text x={P + 6} y={H - P - 6} fontSize="11" fontWeight={600} fill="var(--ink-40)">LOW PRIORITY</text>

        {/* Axes */}
        <text x={W / 2} y={H - 14} textAnchor="middle" fontSize="11" fill="var(--ink-60)">Importance to consumers (1-10) →</text>
        <text x={16} y={H / 2} textAnchor="middle" fontSize="11" fill="var(--ink-60)" transform={`rotate(-90 16 ${H / 2})`}>Crompton brand-imagery share (%) →</text>

        {/* Ticks */}
        {[xLo, (xLo + block.imp_median) / 2, block.imp_median, (block.imp_median + xHi) / 2, xHi].map((t, i) => (
          <g key={`xt${i}`}>
            <line x1={x(t)} y1={H - P} x2={x(t)} y2={H - P + 4} stroke="var(--ink-20)" />
            <text x={x(t)} y={H - P + 16} textAnchor="middle" fontSize="10" fill="var(--ink-60)">{t.toFixed(2)}</text>
          </g>
        ))}
        {[yLo, (yLo + block.own_median) / 2, block.own_median, (block.own_median + yHi) / 2, yHi].map((t, i) => (
          <g key={`yt${i}`}>
            <line x1={P - 4} y1={y(t)} x2={P} y2={y(t)} stroke="var(--ink-20)" />
            <text x={P - 8} y={y(t) + 3} textAnchor="end" fontSize="10" fill="var(--ink-60)">{t.toFixed(0)}%</text>
          </g>
        ))}

        {/* Dots */}
        {block.rows.filter(r =>
          r.importance != null && r.crompton_share != null && r.quadrant !== "missing_data"
        ).map((r) => {
          const c = QUAD_COLOR[r.quadrant];
          const isPriority = r.quadrant === "opportunity" || r.quadrant === "owned_strength";
          return (
            <g key={r.code}>
              <circle cx={x(r.importance!)} cy={y(r.crompton_share!)}
                r={isPriority ? 9 : 6} fill={c} fillOpacity={0.85}
                stroke="white" strokeWidth="1.5" />
              {isPriority && (
                <text x={x(r.importance!) + 12} y={y(r.crompton_share!) + 4}
                  fontSize="10.5" fill={r.quadrant === "opportunity" ? "var(--coral-ink)" : "var(--teal-ink)"}
                  fontWeight={600}>
                  {r.label.length > 32 ? r.label.slice(0, 30) + "…" : r.label}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </section>
  );
}

// Callout lists - white-space + owned-strength shortlist
function CalloutLists({ block }: { block: LensBlock }) {
  const opps = block.rows.filter(r => r.quadrant === "opportunity")
    .sort((a, b) => ((b.importance ?? 0) - (b.crompton_share ?? 0)/100*8) - ((a.importance ?? 0) - (a.crompton_share ?? 0)/100*8));
  const wins = block.rows.filter(r => r.quadrant === "owned_strength")
    .sort((a, b) => (b.importance ?? 0) - (a.importance ?? 0));

  return (
    <section className="callout-row">
      <div className="callout coral">
        <div className="hd"><h3>Marketing white-space</h3><span className="pill coral">{opps.length}</span></div>
        <p className="muted">High consumer importance, low Crompton ownership. These are the campaigns to commission.</p>
        {opps.length === 0 && <div className="muted small">None detected at the median split. Try a different layer.</div>}
        <ul className="callout-list">
          {opps.slice(0, 5).map((r) => {
            const gap = (r.top_competitor_share ?? 0) - (r.crompton_share ?? 0);
            return (
              <li key={r.code}>
                <div className="cl-label">{r.label}</div>
                <div className="cl-meta muted">
                  Importance {r.importance?.toFixed(2)} · Crompton {r.crompton_share?.toFixed(1)}% · {r.top_competitor} owns it ({r.top_competitor_share?.toFixed(1)}%, +{gap.toFixed(1)}pt ahead)
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      <div className="callout teal">
        <div className="hd"><h3>Owned strengths</h3><span className="pill teal">{wins.length}</span></div>
        <p className="muted">Crompton already owns these in the consumer's mind. Defend with continued spend.</p>
        {wins.length === 0 && <div className="muted small">No owned-strengths cluster at the median split.</div>}
        <ul className="callout-list">
          {wins.slice(0, 5).map((r: LensRow) => (
            <li key={r.code}>
              <div className="cl-label">{r.label}</div>
              <div className="cl-meta muted">
                Importance {r.importance?.toFixed(2)} · Crompton {r.crompton_share?.toFixed(1)}% · vs {r.top_competitor} {r.top_competitor_share?.toFixed(1)}%
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
